import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Form } from 'react-bootstrap';
import { FaUser, FaEnvelope, FaPhone, FaCheck } from 'react-icons/fa';
import { ClientLocal } from '../../../../../../../../../../../../../../views/Restaurant/ClientManagement/Clients/types/client.types';
import MiniLoadingSpinner from '../../../../../../../../../../../../../../components/Spinners/MiniLoadingSpinner';
import './ClientSearchBar.scss';

type SearchField = 'name' | 'email' | 'phone';

interface ClientSearchBarProps {
  localId: number;
  allClients: ClientLocal[]; // Pre-loaded clients for client-side filtering
  onClientSelect: (client: ClientLocal) => void;
  placeholder?: string;
  disabled?: boolean;
}

const ClientSearchBar: React.FC<ClientSearchBarProps> = ({
  localId,
  allClients,
  onClientSelect,
  placeholder = 'Buscar cliente...',
  disabled = false
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchField, setSearchField] = useState<SearchField>('name');
  const [results, setResults] = useState<ClientLocal[]>([]);
  const [loading, setLoading] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [selectedClient, setSelectedClient] = useState<ClientLocal | null>(null);
  
  const searchRef = useRef<HTMLDivElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Click outside to close results
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Client-side filtering (no API calls - use pre-loaded allClients)
  const performSearch = useCallback((term: string, field: SearchField) => {
    if (!term || term.trim().length < 2) {
      setResults([]);
      setShowResults(false);
      return;
    }

    setLoading(true);
    
    // Client-side filtering
    const searchLower = term.toLowerCase().trim();
    const filtered = allClients.filter((client) => {
      switch (field) {
        case 'name': {
          const fullName = `${client.username || ''} ${client.middle_name || ''} ${client.last_name || ''}`.toLowerCase();
          return fullName.includes(searchLower);
        }
        case 'email':
          return client.email?.toLowerCase().includes(searchLower) || false;
        case 'phone':
          return client.phone_number?.includes(term.trim()) || false;
        default:
          return false;
      }
    });

    setResults(filtered);
    setShowResults(true);
    setLoading(false);
  }, [allClients]);

  // Handle search input change with debounce
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setSelectedClient(null);

    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Set new timeout for debounced search
    searchTimeoutRef.current = setTimeout(() => {
      performSearch(value, searchField);
    }, 300);
  };

  // Handle field change
  const handleFieldChange = (field: SearchField) => {
    setSearchField(field);
    if (searchTerm.trim().length >= 2) {
      performSearch(searchTerm, field);
    }
  };

  // Handle client selection
  const handleClientClick = (client: ClientLocal) => {
    setSelectedClient(client);
    setSearchTerm(`${client.username} ${client.last_name || ''}`.trim());
    setShowResults(false);
    onClientSelect(client);
  };

  // Clear selection
  const handleClear = () => {
    setSearchTerm('');
    setSelectedClient(null);
    setResults([]);
    setShowResults(false);
  };

  // Get field label
  const getFieldLabel = (field: SearchField) => {
    switch (field) {
      case 'email':
        return 'Email';
      case 'phone':
        return 'Teléfono';
      case 'name':
      default:
        return 'Nombre';
    }
  };

  // Format client display name
  const getClientDisplayName = (client: ClientLocal) => {
    const parts = [client.username, client.middle_name, client.last_name].filter(Boolean);
    return parts.join(' ');
  };

  return (
    <div className="client-search-bar" ref={searchRef}>
      {/* Horizontal Field Selector - Above Search Bar */}
      <div className="horizontal-field-selector">
        <button
          type="button"
          className={`field-tab ${searchField === 'name' ? 'active' : ''}`}
          onClick={() => {
            handleFieldChange('name');
          }}
          disabled={disabled}
        >
          <FaUser />
          <span>Nombre</span>
        </button>
        <button
          type="button"
          className={`field-tab ${searchField === 'email' ? 'active' : ''}`}
          onClick={() => {
            handleFieldChange('email');
          }}
          disabled={disabled}
        >
          <FaEnvelope />
          <span>Email</span>
        </button>
        <button
          type="button"
          className={`field-tab ${searchField === 'phone' ? 'active' : ''}`}
          onClick={() => {
            handleFieldChange('phone');
          }}
          disabled={disabled}
        >
          <FaPhone />
          <span>Teléfono</span>
        </button>
      </div>

      <div className="search-input-container">
        {/* Search Input */}
        <div className="search-input-wrapper">
          <Form.Control
            type="text"
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder={`${placeholder} (por ${getFieldLabel(searchField).toLowerCase()})`}
            className="search-input"
            disabled={disabled}
          />
          {selectedClient && (
            <button
              className="clear-button"
              onClick={handleClear}
              type="button"
              disabled={disabled}
            >
              <FaCheck className="check-icon" />
            </button>
          )}
          {loading && <MiniLoadingSpinner left="auto" />}
        </div>
      </div>

      {/* Search Results Dropdown */}
      {showResults && results.length > 0 && (
        <div className="search-results-dropdown">
          <div className="results-header">
            {results.length} resultado{results.length !== 1 ? 's' : ''} encontrado{results.length !== 1 ? 's' : ''}
          </div>
          <div className="results-list">
            {results.map((client) => (
              <div
                key={client.client_id}
                className={`result-item ${selectedClient?.client_id === client.client_id ? 'selected' : ''}`}
                onClick={() => handleClientClick(client)}
              >
                <div className="client-avatar">
                  {getClientDisplayName(client).charAt(0).toUpperCase()}
                </div>
                <div className="client-info">
                  <div className="client-name">{getClientDisplayName(client)}</div>
                  <div className="client-details">
                    {client.email && (
                      <span className="detail-item">
                        <FaEnvelope /> {client.email}
                      </span>
                    )}
                    {client.phone_number && (
                      <span className="detail-item">
                        <FaPhone /> {client.phone_number}
                      </span>
                    )}
                  </div>
                  {client.membership && (
                    <div className="client-membership">
                      <span className="membership-badge">
                        {client.membership.membership_name} ({client.membership.discount_percentage}% desc.)
                      </span>
                    </div>
                  )}
                </div>
                {selectedClient?.client_id === client.client_id && (
                  <FaCheck className="selected-icon" />
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* No Results Message */}
      {showResults && results.length === 0 && searchTerm.trim().length >= 2 && !loading && (
        <div className="search-results-dropdown">
          <div className="no-results">
            <FaUser />
            <p>No se encontraron clientes</p>
            <small>Intenta con otro término de búsqueda</small>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientSearchBar;
